import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarCheck, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { BucketGrid } from "@/components/BlogSection";
import { ARTICLES } from "@/data/blogArticles";
import { FadeUp, Eyebrow } from "@/lib/motion";

const NEXT_STEPS = [
  "Todd confirms your slot personally — by email or text, usually the same day.",
  "Come with your goals and your numbers — the call is 10 minutes, straight talk, no pressure.",
  "You leave with an honest yes-or-no on what we'd do first — whether or not we end up working together.",
];

const PICKS = [
  "double-your-leads-in-just-3-months",
  "how-to-show-up-in-ai-answers",
  "attract-more-local-customers",
];

export default function ThankYouPage() {
  const picks = PICKS.map((slug) =>
    ARTICLES.find((a) => a.slug === slug)
  ).filter(Boolean);

  return (
    <main data-testid="thank-you-page">
      <Seo
        title="Thank You — Talk Soon | ZD Design"
        description="Thanks for booking a strategy call with ZD Design. Todd will confirm your slot personally — meanwhile, three guides worth your time."
        path="/thank-you"
        noindex
      />
      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <div className="glass flex h-14 w-14 items-center justify-center rounded-xl border border-slate-200">
              <CalendarCheck className="h-6 w-6 text-flame" />
            </div>
            <Eyebrow>Thank you</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              You're on the calendar<span className="text-flame">.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Your request is in — <strong className="font-bold text-slate-900">Todd will confirm your time personally</strong> before the call. No call centers, no runaround: just a plain-language conversation about where your website, search and ads are leaking customers, and what to fix first.
            </p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <ul className="mt-10 max-w-2xl space-y-4">
              {NEXT_STEPS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`thank-you-step-${i}`}
                  className="flex items-start gap-3 text-lg leading-relaxed text-slate-800"
                >
                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-flame" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>While you wait</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
              Three guides worth your time<span className="text-flame">.</span>
            </h2>
          </FadeUp>
          <div className="mt-10">
            <BucketGrid articles={picks} />
          </div>
          <FadeUp delay={0.1}>
            <div className="mt-12 text-center">
              <Link
                to="/"
                data-testid="thank-you-home-link"
                className="inline-flex items-center gap-2 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:text-flame"
              >
                Back to zd.design
                <ArrowUpRight className="h-4 w-4 text-flame" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}